interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code: number;
    details?: any;
    timestamp: string;
  };
  timestamp: string;
}

interface ValidationResult {
  valid: boolean;
  codeId?: string;
  email?: string;
  expiresAt?: string;
  remainingUses?: number;
}

interface SubmitFormData {
  code: string;
  email: string;
  mobile_phone: string;
  first_name: string;
  family_name: string;
  company_name: string;
  ats_system_id?: number | undefined;
  ats_other?: string | undefined;
  industry_ids: number[];
  region_ids: number[];
  locality_ids: number[];
  privacy_consent: boolean;
  marketing_consent: boolean;
  terms_accepted: boolean;
}

interface SubmitResponse {
  profileId: string;
  message: string;
}

interface LookupData {
  activeOptions: {
  industries: Array<{ id: number; name: string; description?: string }>;
  regions: Array<{ id: number; name: string; code?: string; description?: string }>;
  localities: Array<{ id: number; name: string; type: string; parent_id?: number; region_id?: number }>;
  ats_systems: Array<{ id: number; name: string; description?: string; website_url?: string }>;
  }
}


class ApiClient {
  private baseUrl: string;
  
  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://data.rolerelay.com';
  }

  private async fetchWithErrorHandling<T>(
    url: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${url}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error?.message || `HTTP ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Validate access code
  async validateCode(code: string, email?: string): Promise<ValidationResult> {
    const response = await this.fetchWithErrorHandling<ValidationResult>('/validate-code', {
      method: 'POST',
      body: JSON.stringify({ code, email }),
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Code validation failed');
    }

    return response.data!;
  }

  // Submit form data
  async submitForm(formData: SubmitFormData): Promise<SubmitResponse> {
    const response = await this.fetchWithErrorHandling<SubmitResponse>('/submit-form', {
      method: 'POST',
      body: JSON.stringify(formData),
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Form submission failed');
    }

    return response.data!;
  }

  // Get all lookup data at once (most efficient)
  async getAllLookups(): Promise<LookupData> {
    const response = await this.fetchWithErrorHandling<LookupData>('/get-all-lookups');
    
    if (!response.success) {
      console.log(response.error);
      throw new Error(response.error?.message || 'Failed to fetch lookup data');
    }

    return response.data!;
  }

   // Individual lookup endpoints -- here for reference
 async getIndustries() {
    const response = await this.fetchWithErrorHandling('/industries');
    return response.data || [];
  }

  async getRegions() {
    const response = await this.fetchWithErrorHandling('/regions');
    return response.data || [];
  }

  async getLocalities(regionId?: number) {
    const url = regionId ? `/localities?region_id=${regionId}` : '/localities';
    const response = await this.fetchWithErrorHandling(url);
    return response.data || [];
  }

  async getATSSystems() {
    const response = await this.fetchWithErrorHandling('/ats-systems');
    return response.data || [];
  }
}

export const apiClient = new ApiClient();
export type { SubmitFormData, ValidationResult, SubmitResponse, LookupData };
